<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Package Number Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.packageNumber')"
                v-model.trim="filterOptions.package_number"
              />
              <!-- End:: Package Number Input -->

              <!-- Start:: Status Select -->
              <base-select-input
                col="4"
                :optionsList="packageStatuses"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Select -->

              <!-- Start:: Delivery Representative Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.deliveryRepName')"
                v-model.trim="filterOptions.delivery_rep_name"
              />
              <!-- End:: Delivery Representative Name Input -->

              <!-- Start:: From Date Input -->
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from')"
                v-model="filterOptions.from_date"
              />
              <!-- End:: From Date Input -->

              <!-- Start:: To Date Input -->
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to')"
                v-model="filterOptions.to_date"
              />
              <!-- End:: To Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.packageTracking") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:[`item.id`]="{ index }">
          {{
            (paginations.current_page - 1) * paginations.items_per_page +
            index +
            1
          }}
        </template>

        <!-- Start:: Package Number -->
        <template v-slot:[`item.package_number`]="{ item }">
          <span class="text-primary font-weight-bold">
            {{ item.package_number || "-" }}
          </span>
        </template>
        <!-- End:: Package Number -->

        <!-- Start:: Bundle Number -->
        <template v-slot:[`item.bundle_number`]="{ item }">
          <span>
            {{ item.bundle_number || "-" }}
          </span>
        </template>
        <!-- End:: Bundle Number -->

        <!-- Start:: Recipient Name -->
        <template v-slot:[`item.user.name`]="{ item }">
          <span>
            {{ item.user ? item.user.name : "-" }}
          </span>
        </template>
        <!-- End:: Recipient Name -->

        <!-- Start:: Recipient Phone -->
        <template v-slot:[`item.mobile`]="{ item }">
          <span dir="ltr">
            {{ item.mobile || "-" }}
          </span>
        </template>
        <!-- End:: Recipient Phone -->

        <!-- Start:: Address -->
        <template v-slot:[`item.address`]="{ item }">
          <span
            class="text-truncate d-inline-block"
            style="max-width: 200px"
            :title="item.address"
          >
            {{ item.address || "-" }}
          </span>
        </template>
        <!-- End:: Address -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status.text`]="{ item }">
          <span class="status_badge" :class="getStatusClass(item.status.value)">
            {{item.status.text }}
          </span>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Delivery Representative -->
        <template v-slot:[`item.delivery_rep_name`]="{ item }">
          <span>
            {{ item.delivery_rep_name || "-" }}
          </span>
        </template>
        <!-- End:: Delivery Representative -->

        <!-- Start:: Delivery Representative Phone -->
        <template v-slot:[`item.delivery_rep_mobile`]="{ item }">
          <span dir="ltr">
            {{ item.delivery_rep_mobile || "-" }}
          </span>
        </template>
        <!-- End:: Delivery Representative Phone -->

        <!-- Start:: Last Update Time -->
        <template v-slot:[`item.last_status_update`]="{ item }">
          <span>
            {{ formatLastUpdateTime(item.status_history) }}
          </span>
        </template>
        <!-- End:: Last Update Time -->

        <!-- Start:: Notes -->
        <template v-slot:[`item.notes`]="{ item }">
          <span
            v-if="item.notes"
            class="text-truncate d-inline-block"
            style="max-width: 150px; cursor: pointer"
            :title="item.notes"
            @click="showNotesModal(item.notes)"
          >
            {{ item.notes }}
          </span>
          <span v-else>-</span>
        </template>
        <!-- End:: Notes -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="table_actions">
            <a-tooltip placement="top">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button
                type="button"
                class="table_action_btn table_action_btn--view"
                :aria-label="$t('BUTTONS.show')"
                @click="showItem(item)"
              >
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip placement="top">
              <template slot="title">
                <span>{{ $t("BUTTONS.statusHistory") }}</span>
              </template>
              <button
                type="button"
                class="table_action_btn table_action_btn--history"
                :aria-label="$t('BUTTONS.statusHistory')"
                @click="showStatusHistory(item)"
              >
                <i class="fal fa-history"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Notes Modal -->
          <description-modal
            v-if="dialogNotes"
            :modalIsOpen="dialogNotes"
            :modalDesc="selectedNotes"
            @toggleModal="dialogNotes = !dialogNotes"
          />
          <!-- End:: Notes Modal -->

          <!-- Start:: Status History Modal -->
          <v-dialog v-model="dialogStatusHistory" max-width="600px">
            <v-card>
              <v-card-title class="text-h5 justify-center">
                {{ $t("TITLES.statusHistory") }}
              </v-card-title>
              <v-card-text>
                <div
                  v-if="selectedStatusHistory && selectedStatusHistory.length"
                  class="status_history_list"
                >
                  <div
                    v-for="(history, index) in selectedStatusHistory"
                    :key="index"
                    class="status_history_item"
                  >
                    <div
                      class="status_badge"
                      :class="getStatusClass(history.value)"
                    >
                      {{ history.status }}
                    </div>
                    <div class="status_date">
                      {{ formatDate(history.created_at) }}
                    </div>
                  </div>
                </div>
                <p v-else class="text-center text-muted">
                  {{ $t("MESSAGES.noStatusHistory") }}
                </p>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  class="modal_cancel_btn"
                  @click="dialogStatusHistory = false"
                  >{{ $t("BUTTONS.close") }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Status History Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "PackageTracking",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    packageStatuses() {
      return [
        {
          id: "pending",
          name: this.$t("STATUS.pending"),
          value: "pending",
        },
        {
          id: "new",
          name: this.$t("STATUS.new"),
          value: "new",
        },
        {
          id: "picking_up",
          name: this.$t("STATUS.picking_up"),
          value: "picking_up",
        },
        {
          id: "picked_up",
          name: this.$t("STATUS.picked_up"),
          value: "picked_up",
        },
        {
          id: "on_the_way",
          name: this.$t("STATUS.in_transit"),
          value: "on_the_way",
        },
        {
          id: "arrived",
          name: this.$t("STATUS.arrived"),
          value: "arrived",
        },
        {
          id: "delivered",
          name: this.$t("STATUS.delivered"),
          value: "delivered",
        },
        {
          id: "return_requested",
          name: this.$t("STATUS.return_requested"),
          value: "return_requested",
        },
        {
          id: "return_accepted",
          name: this.$t("STATUS.return_accepted"),
          value: "return_accepted",
        },
        {
          id: "under_return",
          name: this.$t("STATUS.returning"),
          value: "under_return",
        },
        {
          id: "returned",
          name: this.$t("STATUS.returned"),
          value: "returned",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        package_number: null,
        status: null,
        delivery_rep_name: null,
        from_date: null,
        to_date: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.packageNumber"),
          value: "package_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.bundleNumber"),
          value: "bundle_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.recipientName"),
          value: "user.name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.recipientPhone"),
          value: "mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.address"),
          value: "address",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status.text",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.deliveryRepName"),
          value: "driver.name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.deliveryRepPhone"),
          value: "driver.mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.lastUpdate"),
          value: "last_status_update",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.notes"),
          value: "notes",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "120px",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogNotes: false,
      selectedNotes: "",
      dialogStatusHistory: false,
      selectedStatusHistory: [],
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Handle Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/packages/tracking",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.package_number = null;
      this.filterOptions.status = null;
      this.filterOptions.delivery_rep_name = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/packages/tracking",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handle Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get packages
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        const params = {
          page: this.paginations.current_page,
          package_number: this.filterOptions.package_number,
          status: this.filterOptions.status?.value,
          search: this.filterOptions.delivery_rep_name,
          from_date: this.filterOptions.from_date,
          to_date: this.filterOptions.to_date,
        };
        let res = await this.$axios({
          method: "GET",
          url: "packages",
          params: params,
        });

        this.loading = false;
        this.tableRows = res.data.data;
        this.paginations.last_page = res.data.meta.last_page;
        this.paginations.items_per_page = res.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Show Item
    showItem(item) {
      this.$router.push({ path: `/packages/show/${item.id}` });
    },
    // End:: Show Item

    // Start:: Show Notes Modal
    showNotesModal(notes) {
      this.dialogNotes = true;
      this.selectedNotes = notes;
    },
    // End:: Show Notes Modal

    // Start:: Show Status History
    showStatusHistory(item) {
      this.dialogStatusHistory = true;
      this.selectedStatusHistory = item.status_history || [];
    },
    // End:: Show Status History

    // Start:: Get Status Text
    getStatusText(statusValue,text) {
      const statusMap = {
        pending: text,
        new: this.$t("STATUS.new"),
        picking_up: this.$t("STATUS.picking_up"),
        picked_up: this.$t("STATUS.picked_up"),
        in_transit: this.$t("STATUS.in_transit"),
        on_the_way: this.$t("STATUS.in_transit"),
        arrived: this.$t("STATUS.arrived"),
        delivered: this.$t("STATUS.delivered"),
        return_requested: this.$t("STATUS.return_requested"),
        return_accepted: this.$t("STATUS.return_accepted"),
        returning: this.$t("STATUS.returning"),
        under_return: this.$t("STATUS.returning"),
        returned: this.$t("STATUS.returned"),
      };
      return statusMap[statusValue] || statusValue;
    },
    // End:: Get Status Text

    // Start:: Get Status Class
    getStatusClass(statusValue) {
      const statusClassMap = {
        pending: "status-pending",
        new: "status-new",
        picking_up: "status-picking-up",
        picked_up: "status-picked-up",
        in_transit: "status-in-transit",
        on_the_way: "status-in-transit",
        arrived: "status-arrived",
        delivered: "status-delivered",
        return_requested: "status-return-requested",
        return_accepted: "status-return-accepted",
        returning: "status-returning",
        under_return: "status-returning",
        returned: "status-returned",
      };
      return statusClassMap[statusValue] || "";
    },
    // End:: Get Status Class

    // Start:: Format Last Update Time
    formatLastUpdateTime(statusHistory) {
      if (!statusHistory || statusHistory.length === 0) {
        return "-";
      }
      const lastHistory = statusHistory[statusHistory.length - 1];
      return this.formatDate(lastHistory.created_at);
    },
    // End:: Format Last Update Time

    // Start:: Format Date Helper
    formatDate(dateString) {
      if (!dateString) return "-";

      const date = new Date(dateString.replace(" ", "T"));

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    // End:: Format Date Helper
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style lang="scss" scoped>
.status_badge {
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  display: inline-block;
  white-space: nowrap;
}

// Status Colors
.status-pending {
  background-color: #ffa500;
  color: white;
}

.status-new {
  background-color: #4caf50;
  color: white;
}

.status-picking-up {
  background-color: #2196f3;
  color: white;
}

.status-picked-up {
  background-color: #00bcd4;
  color: white;
}

.status-in-transit {
  background-color: #9c27b0;
  color: white;
}

.status-arrived {
  background-color: #ff9800;
  color: white;
}

.status-delivered {
  background-color: #4caf50;
  color: white;
}

.status-return-requested {
  background-color: #f44336;
  color: white;
}

.status-return-accepted {
  background-color: #e91e63;
  color: white;
}

.status-returning {
  background-color: #ff5722;
  color: white;
}

.status-returned {
  background-color: #795548;
  color: white;
}

.status_history_list {
  max-height: 400px;
  overflow-y: auto;
}

.status_history_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;

  &:last-child {
    margin-bottom: 0;
  }
}

.status_date {
  color: #757575;
  font-size: 13px;
}

.table_actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 0;
}

.table_action_btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;

  i {
    font-size: 15px;
    transition: color 0.2s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &--view {
    background: var(--soft_main_theme_clr);
    border-color: rgba(239, 52, 5, 0.15);

    i {
      color: var(--main_theme_clr);
    }

    &:hover {
      background: var(--main_theme_clr);
      border-color: var(--main_theme_clr);

      i {
        color: var(--white_clr);
      }
    }
  }

  &--history {
    background: #e8f4fd;
    border-color: rgba(23, 162, 184, 0.2);

    i {
      color: #17a2b8;
    }

    &:hover {
      background: #17a2b8;
      border-color: #17a2b8;

      i {
        color: #fff;
      }
    }
  }
}
</style>
